import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { ArrowLeft, Save, Upload } from "lucide-react";
import { Link } from "react-router-dom";
import { optimizeGlb } from "@/lib/optimizeGlb";
import { convertToUsdz } from "@/lib/convertToUsdz";

const AdminTributeForm = () => {
  const { id } = useParams();
  const isEditing = Boolean(id);
  const navigate = useNavigate();
  const { toast } = useToast();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    slug: "",
    full_name: "",
    date_of_birth: "",
    date_of_death: "",
    residence_region: "",
    headline: "",
    short_tribute: "",
    personality_prompt: "",
    is_published: false,
    cover_image_url: "",
    model_glb_url: "",
    model_usdz_url: "",
    subject_email: "",
  });

  const [coverFile, setCoverFile] = useState<File | null>(null);
  const [modelFile, setModelFile] = useState<File | null>(null);
  const [optimizeStatus, setOptimizeStatus] = useState<string | null>(null);

  useEffect(() => {
    if (isEditing && id) {
      setLoading(true);
      supabase
        .from("tributes")
        .select("slug, full_name, date_of_birth, date_of_death, residence_region, headline, short_tribute, personality_prompt, is_published, cover_image_url, model_glb_url, model_usdz_url")
        .eq("id", id)
        .single()
        .then(({ data, error }) => {
          if (error || !data) {
            toast({ title: "Tribute not found", variant: "destructive" });
            navigate("/admin/tributes");
            return;
          }
          setForm({
            slug: data.slug,
            full_name: data.full_name,
            date_of_birth: data.date_of_birth ?? "",
            date_of_death: data.date_of_death ?? "",
            residence_region: data.residence_region ?? "",
            headline: data.headline || "",
            short_tribute: data.short_tribute || "",
            personality_prompt: (data as Record<string, unknown>).personality_prompt as string || "",
            is_published: data.is_published,
            cover_image_url: data.cover_image_url || "",
            model_glb_url: data.model_glb_url || "",
            model_usdz_url: (data as Record<string, unknown>).model_usdz_url as string || "",
            subject_email: (data as Record<string, unknown>).subject_email as string || "",
          });
          setLoading(false);
        });
    }
  }, [id]);

  const uploadFile = async (file: File, bucket: string, path: string) => {
    const { error } = await supabase.storage.from(bucket).upload(path, file, { upsert: true });
    if (error) throw error;
    const { data } = supabase.storage.from(bucket).getPublicUrl(path);
    return data.publicUrl;
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.slug || !form.full_name) {
      toast({ title: "Slug and full name are required", variant: "destructive" });
      return;
    }

    setSaving(true);
    try {
      let coverUrl = form.cover_image_url;
      let modelUrl = form.model_glb_url;
      let usdzUrl = form.model_usdz_url;

      if (coverFile) {
        coverUrl = await uploadFile(coverFile, "tribute-images", `${form.slug}/cover.jpg`);
      }
      if (modelFile) {
        let optimizedGlb: File = modelFile;

        // Step 1: Optimize GLB (textures → WebP, dedup, prune)
        setOptimizeStatus("Optimizing 3D model...");
        try {
          optimizedGlb = await optimizeGlb(modelFile, (step) => {
            setOptimizeStatus(step);
          });
        } catch (optErr) {
          console.warn("GLB optimization failed, using original:", optErr);
          setOptimizeStatus("Optimization failed — using original...");
        }

        // Step 2: Upload optimized GLB
        setOptimizeStatus("Uploading GLB...");
        modelUrl = await uploadFile(optimizedGlb, "tribute-models", `${form.slug}/model.glb`);

        // Step 3: Convert to USDZ for iOS AR Quick Look
        setOptimizeStatus("Converting to USDZ for iOS AR...");
        try {
          const usdzFile = await convertToUsdz(optimizedGlb, (step) => {
            setOptimizeStatus(step);
          });
          setOptimizeStatus("Uploading USDZ...");
          usdzUrl = await uploadFile(usdzFile, "tribute-models", `${form.slug}/model.usdz`);
        } catch (usdzErr) {
          console.warn("USDZ conversion failed:", usdzErr);
          setOptimizeStatus("USDZ conversion failed — iOS AR may be limited");
          // Non-fatal: GLB still works, just no iOS Quick Look USDZ
        }

        setOptimizeStatus(null);
      }

      const { data: { user } } = await supabase.auth.getUser();

      // Main payload — never includes model_usdz_url so the save can't
      // fail due to schema cache lag on that column.
      const payload = {
        slug: form.slug,
        full_name: form.full_name,
        date_of_birth: form.date_of_birth || null,
        date_of_death: form.date_of_death || null,
        residence_region: form.residence_region || null,
        headline: form.headline || null,
        short_tribute: form.short_tribute || null,
        personality_prompt: form.personality_prompt || null,
        is_published: form.is_published,
        cover_image_url: coverUrl || null,
        model_glb_url: modelUrl || null,
        ...(isEditing ? {} : { user_id: user?.id }),
      };

      let tributeId = id;

      if (isEditing && id) {
        const { error } = await supabase.from("tributes").update(payload).eq("id", id);
        if (error) throw error;
      } else {
        const { data: inserted, error } = await supabase.from("tributes").insert(payload).select("id").single();
        if (error) throw error;
        tributeId = inserted.id;
      }

      // TODO: this dual-write pattern is technical debt from stale generated types.
      // When src/integrations/supabase/types.ts is regenerated to include
      // model_usdz_url, personality_prompt, and subject_email, fold these
      // follow-up updates into the main payload.
      if (usdzUrl && tributeId) {
        const { error: usdzErr } = await supabase
          .from("tributes")
          .update({ model_usdz_url: usdzUrl } as Record<string, unknown>)
          .eq("id", tributeId);
        if (usdzErr) {
          console.warn("Could not save model_usdz_url (schema cache may need refresh):", usdzErr.message);
          toast({
            title: "Tribute saved, but model_usdz_url didn't write",
            description: "Edit and retry, or check schema cache.",
            variant: "destructive",
          });
        }
      }

      if (tributeId) {
        const { error: subjectErr } = await supabase
          .from("tributes")
          .update({ subject_email: form.subject_email || null } as Record<string, unknown>)
          .eq("id", tributeId);
        if (subjectErr) {
          console.warn("Could not save subject_email (schema cache may need refresh):", subjectErr.message);
          toast({
            title: "Tribute saved, but subject_email didn't write",
            description: "Edit and retry, or check schema cache.",
            variant: "destructive",
          });
        }
      }

      // Non-fatal follow-on (mirrors the model_usdz_url / subject_email blocks
      // above): on a PUBLISH save, ask the backend to promote a consent-gated
      // pending voice clone to live. Must NEVER throw out of handleSave or fail
      // the save. The server re-checks admin + consent independently; here we
      // only surface a notice. Draft saves / unpublished creates skip this.
      if (form.is_published === true && tributeId) {
        try {
          const { data: promoteData, error: promoteErr } = await supabase.functions.invoke(
            "promote-voice",
            { body: { tribute_id: tributeId } },
          );
          if (promoteErr) {
            console.warn("promote-voice failed", promoteErr);
          } else if (promoteData?.promoted === true) {
            toast({ title: "Voice activated for this memorial." });
          } else {
            const missing: string[] = promoteData?.missing ?? [];
            const reason: string | undefined = promoteData?.reason;
            const CONSENT_CODES = [
              "no_interview",
              "no_completed_at",
              "no_final_consent_audio",
              "no_statement_id",
            ];
            // Consent precedence: a publish missing BOTH consent and a clone
            // still shows the consent warning (never silently swallowed).
            if (missing.some((m) => CONSENT_CODES.includes(m))) {
              toast({
                title: "Voice not activated",
                description: "Subject's final consent is not on record yet.",
                variant: "destructive",
              });
            } else if (
              reason === "already_has_different_live_voice" ||
              reason === "replacement_required"
            ) {
              toast({
                title: "Voice not activated",
                description:
                  "A different voice is already live. Replacing it requires a separate action.",
                variant: "destructive",
              });
            }
            // else: benign not-ready (e.g. ["no_pending_voice_id"] or empty) —
            // silent; normal for static / clone-less publishes.
          }
        } catch (promoteCatch) {
          console.warn("promote-voice threw", promoteCatch);
        }
      }

      toast({ title: isEditing ? "Tribute updated" : "Tribute created" });
      navigate("/admin/tributes");
    } catch (err: any) {
      toast({ title: "Error saving tribute", description: err.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen bg-background flex items-center justify-center text-foreground">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card">
        <div className="container mx-auto px-6 h-16 flex items-center gap-4">
          <Button variant="ghost" size="sm" asChild>
            <Link to="/admin/tributes"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Link>
          </Button>
          <img src="/ll-mark-256.png" alt="LegacyLens AI" className="h-8" />
          <span className="font-display text-lg font-semibold text-foreground">
            {isEditing ? "Edit Tribute" : "New Tribute"}
          </span>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8 max-w-2xl">
        <form onSubmit={handleSave} className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="slug">Slug *</Label>
              <Input id="slug" value={form.slug} onChange={e => setForm(f => ({ ...f, slug: e.target.value }))} placeholder="syrus-demo" required />
            </div>
            <div>
              <Label htmlFor="full_name">Full Name *</Label>
              <Input id="full_name" value={form.full_name} onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))} placeholder="Syrus Johnson" required />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="date_of_birth">Date of Birth</Label>
              <Input id="date_of_birth" type="date" value={form.date_of_birth} onChange={e => setForm(f => ({ ...f, date_of_birth: e.target.value }))} />
            </div>
            <div>
              <Label htmlFor="date_of_death">Date of Death (optional)</Label>
              <Input id="date_of_death" type="date" value={form.date_of_death} onChange={e => setForm(f => ({ ...f, date_of_death: e.target.value }))} />
              <p className="text-xs text-muted-foreground mt-1">
                Leave blank while the subject is living. Until this is set, the public page shows birth year only.
              </p>
            </div>
          </div>

          <div>
            <Label htmlFor="residence_region">State / region of residence</Label>
            <Input id="residence_region" type="text" value={form.residence_region} onChange={e => setForm(f => ({ ...f, residence_region: e.target.value }))} placeholder="New York, USA" />
          </div>

          <div>
            <Label htmlFor="headline">Headline</Label>
            <Input id="headline" value={form.headline} onChange={e => setForm(f => ({ ...f, headline: e.target.value }))} placeholder="Beloved Father, Musician, Storyteller" />
          </div>

          <div>
            <Label htmlFor="short_tribute">Short Tribute</Label>
            <Textarea id="short_tribute" value={form.short_tribute} onChange={e => setForm(f => ({ ...f, short_tribute: e.target.value }))} rows={5} placeholder="Share a brief tribute..." />
          </div>

          <div>
            <Label htmlFor="personality_prompt">Personality Prompt</Label>
            <p className="text-xs text-muted-foreground mt-1 mb-2">
              Describe this person's voice, memories, stories, and personality in detail. The more specific, the better the AI conversation will be.
            </p>
            <Textarea id="personality_prompt" value={form.personality_prompt} onChange={e => setForm(f => ({ ...f, personality_prompt: e.target.value }))} rows={10} placeholder="Example: Syrus always called everyone 'buddy' and loved telling stories about flying his Cessna over the farm at sunset. He had a dry sense of humor and would often pause before delivering a punchline..." />
          </div>

          <div>
            <Label htmlFor="cover_image">Cover Image</Label>
            <div className="flex items-center gap-3 mt-1">
              <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 rounded-md border border-input bg-background text-sm hover:bg-accent transition-colors">
                <Upload className="w-4 h-4" />
                {coverFile ? coverFile.name : "Choose image"}
                <input id="cover_image" type="file" accept="image/*" className="hidden" onChange={e => setCoverFile(e.target.files?.[0] || null)} />
              </label>
              {form.cover_image_url && !coverFile && <span className="text-xs text-muted-foreground truncate max-w-[200px]">Current: uploaded</span>}
            </div>
          </div>

          <div>
            <Label htmlFor="model_file">3D Model (.glb)</Label>
            <div className="flex items-center gap-3 mt-1">
              <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 rounded-md border border-input bg-background text-sm hover:bg-accent transition-colors">
                <Upload className="w-4 h-4" />
                {modelFile ? modelFile.name : "Choose .glb file"}
                <input id="model_file" type="file" accept=".glb" className="hidden" onChange={e => setModelFile(e.target.files?.[0] || null)} />
              </label>
              {form.model_glb_url && !modelFile && <span className="text-xs text-muted-foreground truncate max-w-[200px]">Current: uploaded</span>}
            </div>
            {optimizeStatus && (
              <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" className="animate-spin">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2.5" className="opacity-20" />
                  <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
                </svg>
                <span>{optimizeStatus}</span>
              </div>
            )}
          </div>

          <div className="border-t pt-4 mt-4">
            <h3 className="text-sm font-semibold text-foreground mb-3">Notification contact</h3>
            <div>
              <Label htmlFor="subject_email">Subject email (notification recipient)</Label>
              <p className="text-xs text-muted-foreground mt-1 mb-2">
                Temporary admin-entry field — will move to the subject's own consent step in the interview flow.
              </p>
              <Input id="subject_email" type="email" value={form.subject_email} onChange={e => setForm(f => ({ ...f, subject_email: e.target.value }))} placeholder="subject@example.com" />
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Switch id="is_published" checked={form.is_published} onCheckedChange={v => setForm(f => ({ ...f, is_published: v }))} />
            <Label htmlFor="is_published">Published</Label>
          </div>

          <Button type="submit" size="lg" disabled={saving} className="w-full">
            <Save className="w-4 h-4 mr-2" />
            {saving ? "Saving..." : isEditing ? "Update Tribute" : "Create Tribute"}
          </Button>
        </form>
      </main>
    </div>
  );
};

export default AdminTributeForm;
